/**
 * PipelineOutputsDrawer — Right-side drawer listing all pipeline output tables.
 *
 * Shows every non-exploratory completed transform with its output table,
 * allowing expandable column preview with column selection, inline data preview,
 * format-selectable downloads, and multi-table export to connector.
 *
 * Two modes:
 * - Browse (default): list outputs, expand columns, preview data
 * - Review: after selecting "Export to Connector", shows per-table summary with
 *   column checkboxes, validation results, and "Approve & Export" button
 */
import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  FileDownloadOutlined as DownloadIcon,
  CloudUpload as ExportIcon,
  SelectAll as SelectAllIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Visibility as PreviewIcon,
  VisibilityOff as HidePreviewIcon,
  CheckCircleOutline as PassIcon,
  WarningAmber as WarnIcon,
  ErrorOutline as ErrorIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { downloadWorkspaceTable, downloadAssemblyOutput, previewWorkspaceTable } from '../../services/workspaceApi';
import {
  fetchReadiness,
  resolvePostMappingFindings,
  uploadLookupFile,
} from '../../services/readinessApi';
import { useNotifications } from '../../context/NotificationContext';
import HierarchicalPreview from './HierarchicalPreview';
import { ReadinessChip, chipPropsFromFindings } from './readiness/ReadinessChip';
import ReadinessFindingCard from './readiness/ReadinessFindingCard';
import { useResolveAutoRefresh } from './readiness/useResolveAutoRefresh';
import { aggregateByRootCause } from '../../types/readiness';
import type {
  ReadinessFinding,
  ReadinessResolutionRequest,
  ReadinessStatus,
} from '../../types/readiness';
import { getTypeIcon, dtypeLabel } from '../common/columnTypeUtils';
import type { TableValidationResult } from '../../services/connectorApi';
import type { OutputQualitySummary } from './hooks/useExportPipeline';
import {
  Build as CorrectionIcon,
  HelpOutline as NoReviewIcon,
} from '@mui/icons-material';

export interface OutputTableInfo {
  operationId: string;
  outputRef: string;          // workspace table/view name (e.g., "__mapped_subjects__")
  displayName?: string;       // clean name for mapped outputs ("subjects" not "__mapped_subjects__")
  description: string;
  stepType: string;
  rowCount?: number;
  columnCount?: number;
  phase?: 'pre_mapping' | 'post_mapping';
  error?: string;             // materialization error message for failed views
  /** Present for hierarchical assembly outputs — assembly:// storage path. */
  assembledOutputPath?: string;
  /** Serialization format for assembled output ("xml", "json"). */
  outputFormat?: string;
  /** Assembled output byte size. */
  byteSize?: number;
}

export interface PipelineOutputsDrawerProps {
  open: boolean;
  onClose: () => void;
  outputs: OutputTableInfo[];
  sessionId: string;
  onExportSelected: (outputRefs: string[], columnFilters?: Record<string, string[]>) => void;
  /** Per-table validation results from pre-export check. */
  validationResults?: Map<string, TableValidationResult>;
  /** Callback to trigger validation against a selected connector. */
  onValidate?: (outputRefs: string[], connectorId: string) => void;
  /** Name of selected destination connector (shown in review mode). */
  connectorName?: string;
  /** Per-output quality summaries from review data. */
  qualitySummaries?: Map<string, OutputQualitySummary>;
  /** Whether the target schema has an OutputStructure (hierarchical output). */
  hasOutputStructure?: boolean;
  /** Callback to trigger on-demand assembly + download. */
  onAssembleAndDownload?: () => void;
  /** Whether assembly is currently in progress. */
  assembling?: boolean;
  /** Validation endpoint error message. */
  validationError?: string | null;
  /** Callback to clear validation error and retry. */
  onClearValidationError?: () => void;
  /** Persist current selection as the canvas "Pipeline Output" node. */
  onSaveSelection?: (outputRefs: string[]) => void | Promise<void>;
  /** Currently persisted selection (from workspace_meta) so the drawer
   * pre-checks rows that were last saved. */
  initialSelectedRefs?: string[];
}

const STEP_TYPE_COLORS: Record<string, string> = {
  sql_transform: '#2563eb',
  python_transform: '#059669',
  ai_transform: '#7c3aed',
  deduplicate: '#d97706',
  filter: '#0891b2',
  join: '#db2777',
  mapped_output: '#0d9488',
};

interface ExpandedState {
  columns?: Array<{ name: string; dtype: string }>;
  columnsLoading?: boolean;
  columnsError?: string;
  previewRows?: Record<string, any>[];
  previewLoading?: boolean;
  previewVisible?: boolean;
}

const PipelineOutputsDrawer: React.FC<PipelineOutputsDrawerProps> = ({
  open,
  onClose,
  outputs,
  sessionId,
  onExportSelected,
  validationResults,
  connectorName,
  qualitySummaries,
  hasOutputStructure,
  onAssembleAndDownload,
  assembling,
  validationError,
  onClearValidationError,
  onSaveSelection,
  initialSelectedRefs,
}) => {
  const { showToast } = useNotifications();
  const [selectedRefs, setSelectedRefs] = useState<Set<string>>(
    () => new Set(initialSelectedRefs || []),
  );
  const [savingSelection, setSavingSelection] = useState(false);

  // Readiness rollout — the drawer is a *mixed* surface: tabular
  // outputs (target_table / pipeline_output consumers) and
  // hierarchical XML (hierarchical_assembly consumer) can coexist in
  // the same list. Querying a single consumer_kind for the top-line
  // chip produces the well-known contradiction where the chip says
  // "Ready" while the only XML row in the drawer is actually
  // not_ready (its assembly inspector says so). Fix: detect which
  // consumer kinds are represented by the rows and fetch all of
  // them, then derive the chip from the union. ``applicable_consumers``
  // on each finding already prevents cross-talk, so the union is
  // safe even when both kinds return overlapping mapping-phase
  // findings.
  const hasHierarchicalRow = React.useMemo(
    () => outputs.some((o) => !!o.assembledOutputPath),
    [outputs],
  );
  const [readinessFindings, setReadinessFindings] = useState<
    ReadinessFinding[]
  >([]);
  const [readinessStatus, setReadinessStatus] =
    useState<ReadinessStatus | null>(null);
  const refreshReadiness = React.useCallback(() => {
    if (!sessionId) return () => {};
    let cancelled = false;
    const kinds: Array<'pipeline_output' | 'hierarchical_assembly'> = [
      'pipeline_output',
    ];
    if (hasHierarchicalRow) kinds.push('hierarchical_assembly');
    Promise.all(
      kinds.map((k) =>
        fetchReadiness({ sessionId, consumerKind: k }).catch(() => null),
      ),
    )
      .then((responses) => {
        if (cancelled) return;
        const all: ReadinessFinding[] = [];
        const seen = new Set<string>();
        let worst: ReadinessStatus | null = null;
        const rank = (s: ReadinessStatus | null) =>
          s === 'not_ready' ? 2 : s === 'ready_with_notes' ? 1 : 0;
        for (const resp of responses) {
          if (!resp) continue;
          for (const f of resp.findings || []) {
            // Dedup across kinds — the same mapping-phase finding can
            // surface on both pipeline_output and hierarchical_assembly.
            const key = `${f.code}|${f.target_path}|${f.target_element}`;
            if (seen.has(key)) continue;
            seen.add(key);
            all.push(f);
          }
          if (rank(resp.status) > rank(worst)) worst = resp.status;
        }
        setReadinessFindings(all);
        setReadinessStatus(worst);
      })
      .catch(() => {
        if (cancelled) return;
        setReadinessFindings([]);
        setReadinessStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, hasHierarchicalRow]);

  React.useEffect(() => {
    if (!open) return;
    return refreshReadiness();
  }, [open, refreshReadiness]);
  // SSE-driven refresh — same pattern as the assembly inspector / review pane.
  useResolveAutoRefresh(sessionId, refreshReadiness);

  // Plan §"Stale readiness" — re-fetch on tab/panel return.
  React.useEffect(() => {
    if (!open) return;
    const onFocus = () => refreshReadiness();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshReadiness();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [open, refreshReadiness]);
  React.useEffect(() => {
    // Sync from persisted selection whenever it changes (drawer reopened
    // after meta was updated elsewhere, etc.). Local toggles override until
    // the next external update.
    if (initialSelectedRefs) {
      setSelectedRefs(new Set(initialSelectedRefs));
    }
  }, [initialSelectedRefs?.join('|')]);
  const [expandedStates, setExpandedStates] = useState<Map<string, ExpandedState>>(new Map());
  const [expandedRefs, setExpandedRefs] = useState<Set<string>>(new Set());
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [downloadMenuTable, setDownloadMenuTable] = useState('');
  const [reviewStep, setReviewStep] = useState<'browse' | 'review'>('browse');
  // Per-table column selection: table outputRef → selected column names
  const [columnFilters, setColumnFilters] = useState<Map<string, Set<string>>>(new Map());

  // Detect if outputs are post-mapping (all or some have phase === 'post_mapping')
  const isMappedPhase = outputs.length > 0 && outputs.some((o) => o.phase === 'post_mapping');
  const mappedOutputs = outputs.filter((o) => o.phase === 'post_mapping');
  const transformOutputs = outputs.filter((o) => o.phase !== 'post_mapping');

  const toggleRef = useCallback((ref: string) => {
    setSelectedRefs((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedRefs((prev) => {
      if (prev.size === outputs.length) return new Set();
      return new Set(outputs.map((o) => o.outputRef));
    });
  }, [outputs]);

  const toggleColumnFilter = useCallback((tableRef: string, colName: string) => {
    setColumnFilters((prev) => {
      const next = new Map(prev);
      const current = next.get(tableRef);
      if (!current) return next; // columns not loaded yet
      const updated = new Set(current);
      if (updated.has(colName)) updated.delete(colName);
      else updated.add(colName);
      next.set(tableRef, updated);
      return next;
    });
  }, []);

  const toggleExpand = useCallback(
    async (ref: string) => {
      setExpandedRefs((prev) => {
        const next = new Set(prev);
        if (next.has(ref)) {
          next.delete(ref);
        } else {
          next.add(ref);
        }
        return next;
      });

      const failedOutput = outputs.find((o) => o.outputRef === ref && o.error);
      if (failedOutput) return;

      // Fetch columns on first expand — skip for assembly byte outputs,
      // which have no tabular columns and would 404 via the table preview API.
      const state = expandedStates.get(ref);
      if (!state?.columns && !state?.columnsLoading && !ref.startsWith('assembly://')) {
        setExpandedStates((prev) => {
          const next = new Map(prev);
          next.set(ref, { ...(next.get(ref) || {}), columnsLoading: true });
          return next;
        });
        try {
          const preview = await previewWorkspaceTable(sessionId, ref, 1);
          const cols = preview.columns.map((col) => {
            const sampleVal = preview.rows[0]?.[col];
            let dtype = 'VARCHAR';
            if (sampleVal !== null && sampleVal !== undefined) {
              if (typeof sampleVal === 'number') dtype = Number.isInteger(sampleVal) ? 'INTEGER' : 'DOUBLE';
              else if (typeof sampleVal === 'boolean') dtype = 'BOOLEAN';
            }
            return { name: col, dtype };
          });
          setExpandedStates((prev) => {
            const next = new Map(prev);
            next.set(ref, { ...(next.get(ref) || {}), columns: cols, columnsLoading: false });
            return next;
          });
          // Initialize column filter: all columns selected by default
          setColumnFilters((prev) => {
            if (prev.has(ref)) return prev;
            const next = new Map(prev);
            next.set(ref, new Set(cols.map((c) => c.name)));
            return next;
          });
        } catch (err: any) {
          setExpandedStates((prev) => {
            const next = new Map(prev);
            next.set(ref, {
              ...(next.get(ref) || {}),
              columns: [],
              columnsLoading: false,
              columnsError: err?.message || 'Failed to load columns',
            });
            return next;
          });
        }
      }
    },
    [sessionId, expandedStates, outputs],
  );

  const togglePreview = useCallback(
    async (ref: string) => {
      const state = expandedStates.get(ref);
      if (state?.previewVisible) {
        setExpandedStates((prev) => {
          const next = new Map(prev);
          next.set(ref, { ...(next.get(ref) || {}), previewVisible: false });
          return next;
        });
        return;
      }

      setExpandedStates((prev) => {
        const next = new Map(prev);
        next.set(ref, { ...(next.get(ref) || {}), previewVisible: true, previewLoading: true });
        return next;
      });
      // Assembly outputs (assembly://…) render via HierarchicalPreview which
      // fetches its own content via the /assembly/preview endpoint.  Skip the
      // table-based preview fetch — it would return 404 for byte outputs.
      if (ref.startsWith('assembly://')) {
        setExpandedStates((prev) => {
          const next = new Map(prev);
          next.set(ref, { ...(next.get(ref) || {}), previewLoading: false });
          return next;
        });
        return;
      }
      try {
        const preview = await previewWorkspaceTable(sessionId, ref, 20);
        setExpandedStates((prev) => {
          const next = new Map(prev);
          next.set(ref, { ...(next.get(ref) || {}), previewRows: preview.rows, previewLoading: false });
          return next;
        });
      } catch {
        setExpandedStates((prev) => {
          const next = new Map(prev);
          next.set(ref, { ...(next.get(ref) || {}), previewRows: [], previewLoading: false });
          return next;
        });
      }
    },
    [sessionId, expandedStates],
  );

  const handleDownload = useCallback(
    (table: string, format: 'csv' | 'parquet') => {
      if (!sessionId) return;
      downloadWorkspaceTable(sessionId, table, format).catch((err) => {
        showToast('Download failed', err?.message || 'Failed to download workspace table', 'error');
      });
      setDownloadMenuAnchor(null);
    },
    [sessionId, showToast],
  );

  const handleExportSelected = useCallback(() => {
    const refs = Array.from(selectedRefs);
    if (refs.length === 0) return;

    // Build column filters from selection
    const filters: Record<string, string[]> = {};
    for (const ref of refs) {
      const selected = columnFilters.get(ref);
      const allCols = expandedStates.get(ref)?.columns;
      if (selected && allCols && selected.size < allCols.length) {
        filters[ref] = Array.from(selected);
      }
    }

    onExportSelected(refs, Object.keys(filters).length > 0 ? filters : undefined);
  }, [selectedRefs, columnFilters, expandedStates, onExportSelected]);

  const handleEnterReview = useCallback(() => {
    if (selectedRefs.size === 0) {
      // Auto-select all if none selected
      setSelectedRefs(new Set(outputs.map((o) => o.outputRef)));
    }
    setReviewStep('review');
  }, [selectedRefs, outputs]);

  const allSelected = selectedRefs.size === outputs.length && outputs.length > 0;

  // Per-selection quality/validation checks — only block if *selected* tables have issues
  const selectedHasQualityFailures = qualitySummaries
    ? Array.from(selectedRefs).some((ref) => qualitySummaries.get(ref)?.overallStatus === 'fail')
    : false;
  const selectedHasValidationErrors = validationResults
    ? Array.from(selectedRefs).some((ref) => validationResults.get(ref)?.status === 'fail')
    : false;
  const selectedHasQualityWarnings = qualitySummaries
    ? Array.from(selectedRefs).some((ref) => {
        const s = qualitySummaries.get(ref)?.overallStatus;
        return s === 'warn' || s === 'no_review';
      })
    : false;
  const hasIssues = selectedHasValidationErrors || selectedHasQualityFailures;
  const canExport = true; // Allow export even with issues
  const showOverride = selectedHasQualityWarnings || hasIssues;

  // Count failing selected tables for user hint
  const failingSelectedCount = qualitySummaries
    ? Array.from(selectedRefs).filter((ref) => qualitySummaries.get(ref)?.overallStatus === 'fail').length
    : 0;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ sx: { top: 64 } }}
      PaperProps={{
        sx: { width: 440, display: 'flex', flexDirection: 'column', top: 64, height: 'calc(100% - 64px)' },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {reviewStep === 'review' && (
          <IconButton size="small" onClick={() => setReviewStep('browse')} sx={{ mr: -0.5 }}>
            <BackIcon fontSize="small" />
          </IconButton>
        )}
        <DownloadIcon sx={{ color: isMappedPhase ? '#0d9488' : '#2563eb' }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
          {reviewStep === 'review' ? 'Review & Export' : 'Pipeline Outputs'}
        </Typography>
        <Chip
          label={`${outputs.length} table${outputs.length !== 1 ? 's' : ''}`}
          size="small"
          sx={{ fontSize: 11, height: 22, fontWeight: 600 }}
        />
        {/* Readiness chip — surfaces blockers across the pipeline output
            consumer. The full list with cards lives in the per-table
            inline panels; this is the headline. */}
        {readinessStatus && (
          <ReadinessChip
            state={readinessStatus}
            count={chipPropsFromFindings(aggregateByRootCause(readinessFindings)).count}
            size="small"
          />
        )}
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Readiness summary banner — shown when blockers are present so
          the user understands why per-table downloads may be refused.
          Plain-English copy; never references diagnostic codes. */}
      {readinessStatus === 'not_ready' && readinessFindings.length > 0 && (
        <Box
          sx={{
            mx: 1.5,
            mt: 1,
            p: 1.25,
            borderRadius: 1,
            bgcolor: alpha('#dc2626', 0.06),
            border: '1px solid',
            borderColor: alpha('#dc2626', 0.25),
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: 12, fontWeight: 600, color: '#991b1b' }}
          >
            {(() => {
              // Same root-cause aggregation the chip uses, so the
              // banner count and chip count can't drift.
              const aggBlockers = aggregateByRootCause(readinessFindings).filter(
                (f) => f.severity === 'blocker',
              );
              return `${aggBlockers.length} thing${aggBlockers.length === 1 ? '' : 's'} to fix before exporting`;
            })()}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: 11, color: '#7f1d1d' }}>
            Resolve the issues below to enable export. Tables marked
            ready can still be downloaded individually.
          </Typography>
          {sessionId && (
            <Stack spacing={0.75} sx={{ mt: 1 }}>
              {aggregateByRootCause(readinessFindings)
                .filter((f) => f.severity === 'blocker')
                .slice(0, 20)
                .map((f) => (
                  <ReadinessFindingCard
                    key={`${f.code}-${f.target_path}`}
                    finding={f}
                    onResolve={async (req: ReadinessResolutionRequest) => {
                      const resp = await resolvePostMappingFindings(
                        sessionId,
                        [req],
                      );
                      if (resp.errors && resp.errors.length > 0) {
                        const first = resp.errors[0];
                        throw new Error(
                          first?.reason || 'Could not apply your choice.',
                        );
                      }
                      showToast(
                        'Saved',
                        resp.next_step || 'Your choice has been recorded.',
                        'success',
                      );
                      refreshReadiness();
                      // SSE rebroadcast drives subsequent refresh when
                      // the bg dispatch lands.
                      return {
                        dispatched: resp.dispatched || {},
                        next_step: resp.next_step,
                      };
                    }}
                    onUploadLookup={async (file: File) => {
                      const r = await uploadLookupFile(sessionId, file);
                      return { alias: r.table_name };
                    }}
                  />
                ))}
            </Stack>
          )}
        </Box>
      )}

      {/* Export as XML button — shown when target schema has output_structure */}
      {hasOutputStructure && onAssembleAndDownload && outputs.length > 0 && (
        <Box sx={{ px: 1.5, pb: 1 }}>
          <Button
            variant="contained"
            fullWidth
            size="small"
            disabled={assembling}
            onClick={onAssembleAndDownload}
            startIcon={assembling ? <CircularProgress size={16} /> : <DownloadIcon />}
            sx={{
              bgcolor: '#7c3aed',
              '&:hover': { bgcolor: '#6d28d9' },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {assembling ? 'Assembling XML...' : 'Export as XML'}
          </Button>
        </Box>
      )}

      {/* Output list */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 1.5, py: 1 }}>
        {outputs.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
            No output tables yet
          </Typography>
        )}

        {/* Review mode: connector + validation summary */}
        {reviewStep === 'review' && connectorName && (
          <Box sx={{ mb: 1.5, p: 1, borderRadius: 1, bgcolor: alpha('#7c3aed', 0.04), border: `1px solid ${alpha('#7c3aed', 0.15)}` }}>
            <Typography variant="caption" sx={{ fontSize: 10, color: '#64748b' }}>
              Destination
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: 12 }}>
              {connectorName}
            </Typography>
          </Box>
        )}

        {/* Validation error banner */}
        {validationError && (
          <Box sx={{
            mb: 1.5, p: 1.5, borderRadius: 1.5,
            bgcolor: alpha('#dc2626', 0.06), border: `1px solid ${alpha('#dc2626', 0.2)}`,
            display: 'flex', alignItems: 'center', gap: 1,
          }}>
            <ErrorIcon sx={{ fontSize: 18, color: '#dc2626' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11, color: '#dc2626', display: 'block' }}>
                Validation Failed
              </Typography>
              <Typography variant="caption" sx={{ fontSize: 10, color: '#64748b' }}>
                {validationError}
              </Typography>
            </Box>
            {onClearValidationError && (
              <Button size="small" onClick={onClearValidationError} sx={{ fontSize: 10, textTransform: 'none', color: '#dc2626' }}>
                Retry
              </Button>
            )}
          </Box>
        )}

        {/* Quality summary banner in review mode */}
        {reviewStep === 'review' && qualitySummaries && qualitySummaries.size > 0 && (() => {
          const summaries = Array.from(qualitySummaries.values());
          const hasFailures = summaries.some((s) => s.overallStatus === 'fail');
          const hasWarnings = summaries.some((s) => s.overallStatus === 'warn');
          const hasNoReview = summaries.some((s) => s.overallStatus === 'no_review');
          const allPass = summaries.every((s) => s.overallStatus === 'pass');

          const bannerColor = hasFailures ? '#dc2626' : hasWarnings ? '#d97706' : hasNoReview ? '#64748b' : '#059669';
          const BannerIcon = hasFailures ? ErrorIcon : hasWarnings ? WarnIcon : hasNoReview ? NoReviewIcon : PassIcon;
          const failCount = summaries.filter((s) => s.overallStatus === 'fail').length;
          const bannerText = hasFailures
            ? `${failCount} output(s) have quality issues — deselect them to export the rest`
            : hasWarnings
              ? `${summaries.filter((s) => s.overallStatus === 'warn').length} output(s) have warnings`
              : hasNoReview
                ? 'Some outputs have no review data'
                : 'All quality checks passed';

          return (
            <Box sx={{
              mb: 1.5, p: 1.5, borderRadius: 1.5,
              bgcolor: alpha(bannerColor, 0.06),
              border: `1px solid ${alpha(bannerColor, 0.2)}`,
              display: 'flex', alignItems: 'center', gap: 1,
            }}>
              <BannerIcon sx={{ fontSize: 18, color: bannerColor }} />
              <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11, color: bannerColor }}>
                {bannerText}
              </Typography>
            </Box>
          );
        })()}

        {/* Validation results summary in review mode */}
        {reviewStep === 'review' && validationResults && validationResults.size > 0 && (
          <Box sx={{ mb: 1.5 }}>
            {Array.from(validationResults.entries()).map(([table, result]) => (
              <Box
                key={table}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  py: 0.5,
                  px: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  bgcolor: result.status === 'fail' ? alpha('#dc2626', 0.05) : result.status === 'warn' ? alpha('#d97706', 0.05) : alpha('#059669', 0.05),
                  border: `1px solid ${result.status === 'fail' ? alpha('#dc2626', 0.2) : result.status === 'warn' ? alpha('#d97706', 0.2) : alpha('#059669', 0.2)}`,
                }}
              >
                {result.status === 'pass' && <PassIcon sx={{ fontSize: 14, color: '#059669' }} />}
                {result.status === 'warn' && <WarnIcon sx={{ fontSize: 14, color: '#d97706' }} />}
                {result.status === 'fail' && <ErrorIcon sx={{ fontSize: 14, color: '#dc2626' }} />}
                <Typography variant="caption" sx={{ fontSize: 11, flex: 1, fontWeight: 600 }}>
                  {table}
                </Typography>
                {result.issues.length > 0 && (
                  <Typography variant="caption" sx={{ fontSize: 10, color: '#64748b' }}>
                    {result.issues.filter((i) => i.severity !== 'ok').length} issue{result.issues.filter((i) => i.severity !== 'ok').length !== 1 ? 's' : ''}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Mapped Outputs section */}
        {isMappedPhase && mappedOutputs.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ fontSize: 11, color: '#0d9488', textTransform: 'uppercase', letterSpacing: 0.5, px: 0.5, mb: 0.5, display: 'block' }}
            >
              Mapped Outputs
            </Typography>
          </Box>
        )}
        {(isMappedPhase ? mappedOutputs : outputs).map((out) => {
          const isFailed = !!out.error;
          const isAssembled = !!out.assembledOutputPath;
          const chipColor = isFailed ? '#dc2626' : isAssembled ? '#a855f7' : (STEP_TYPE_COLORS[out.stepType] || '#64748b');
          const isChecked = selectedRefs.has(out.outputRef);
          const isExpanded = expandedRefs.has(out.outputRef);
          const state = expandedStates.get(out.outputRef);
          const selectedCols = columnFilters.get(out.outputRef);

          // In review mode, only show selected tables
          if (reviewStep === 'review' && !isChecked) return null;

          return (
            <Box key={out.outputRef || out.operationId} sx={{ mb: 0.75 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.75,
                  py: 1,
                  px: 1,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: isFailed ? alpha('#dc2626', 0.3) : isChecked ? alpha('#2563eb', 0.3) : 'divider',
                  bgcolor: isFailed ? alpha('#dc2626', 0.03) : isChecked ? alpha('#2563eb', 0.03) : 'transparent',
                  transition: 'border-color 0.15s, background-color 0.15s',
                  cursor: 'pointer',
                }}
                onClick={() => toggleExpand(out.outputRef)}
              >
                {reviewStep === 'browse' && (
                  <Checkbox
                    size="small"
                    checked={isChecked}
                    disabled={isFailed}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleRef(out.outputRef);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ p: 0.25, mt: 0.25 }}
                  />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Tooltip title={out.outputRef} placement="top" arrow disableInteractive>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        fontFamily: '"Fira Code", monospace',
                        fontSize: 12,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {out.displayName || out.outputRef}
                    </Typography>
                  </Tooltip>
                  <Tooltip title={out.description} placement="bottom" arrow disableInteractive>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: 10,
                        color: '#64748b',
                        display: 'block',
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {out.description}
                    </Typography>
                  </Tooltip>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <Chip
                      label={isFailed ? 'FAILED' : out.stepType.replace(/_/g, ' ')}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: 9,
                        fontWeight: 700,
                        bgcolor: alpha(chipColor, 0.1),
                        color: chipColor,
                        textTransform: 'uppercase',
                      }}
                    />
                    {isFailed ? (
                      <Tooltip title={out.error} placement="bottom" arrow>
                        <Typography variant="caption" sx={{ fontSize: 10, color: '#dc2626' }}>
                          {out.error!.length > 60 ? out.error!.slice(0, 60) + '…' : out.error}
                        </Typography>
                      </Tooltip>
                    ) : out.rowCount != null && (
                      <Typography variant="caption" sx={{ fontSize: 10, color: '#94a3b8' }}>
                        {out.rowCount.toLocaleString()} rows
                        {out.columnCount != null && ` · ${out.columnCount} cols`}
                      </Typography>
                    )}
                    {/* Quality chip */}
                    {(() => {
                      const qs = qualitySummaries?.get(out.outputRef);
                      if (!qs) return null;
                      if (qs.overallStatus === 'pass') return (
                        <PassIcon sx={{ fontSize: 12, color: '#059669' }} />
                      );
                      if (qs.overallStatus === 'warn') return (
                        <Chip icon={<WarnIcon sx={{ fontSize: 10 }} />} label={`${qs.warnings.length}`} size="small"
                          sx={{ height: 16, fontSize: 9, fontWeight: 600, bgcolor: alpha('#d97706', 0.1), color: '#d97706', '& .MuiChip-icon': { ml: 0.25 } }} />
                      );
                      if (qs.overallStatus === 'fail') return (
                        <Chip icon={<ErrorIcon sx={{ fontSize: 10 }} />} label={`${qs.failedChecks.length}`} size="small"
                          sx={{ height: 16, fontSize: 9, fontWeight: 600, bgcolor: alpha('#dc2626', 0.1), color: '#dc2626', '& .MuiChip-icon': { ml: 0.25 } }} />
                      );
                      if (qs.overallStatus === 'no_review') return (
                        <NoReviewIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                      );
                      return null;
                    })()}
                    {/* Column filter summary */}
                    {selectedCols && state?.columns && selectedCols.size < state.columns.length && (
                      <Chip
                        label={`${selectedCols.size}/${state.columns.length} cols`}
                        size="small"
                        sx={{ height: 16, fontSize: 9, fontWeight: 600, bgcolor: alpha('#d97706', 0.1), color: '#d97706' }}
                      />
                    )}
                  </Box>
                </Box>
                {/* Download + expand buttons */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flexShrink: 0 }}>
                  <Tooltip title="Download" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDownloadMenuTable(out.outputRef);
                        setDownloadMenuAnchor(e.currentTarget);
                      }}
                      sx={{ p: 0.5, color: '#2563eb' }}
                    >
                      <DownloadIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size="small"
                    sx={{ p: 0.25 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(out.outputRef);
                    }}
                  >
                    {isExpanded ? <CollapseIcon sx={{ fontSize: 16 }} /> : <ExpandIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Box>
              </Box>

              {/* Expanded content */}
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    ml: reviewStep === 'browse' ? 4.5 : 1,
                    mr: 1,
                    mt: 0.5,
                    p: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: '#fafbfc',
                  }}
                >
                  {isFailed && out.error && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: 'block', fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word', mb: state?.columns || state?.columnsLoading ? 0.75 : 0 }}
                    >
                      {out.error}
                    </Typography>
                  )}

                  {state?.columnsLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                      <CircularProgress size={14} />
                      <Typography variant="caption" color="text.secondary">Loading columns...</Typography>
                    </Box>
                  )}

                  {state?.columns && state.columns.length > 0 && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                        <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11, color: 'text.secondary' }}>
                          Columns ({selectedCols ? `${selectedCols.size}/${state.columns.length}` : state.columns.length})
                        </Typography>
                        <Tooltip title={state.previewVisible ? 'Hide preview' : 'Preview data'}>
                          <IconButton size="small" onClick={() => togglePreview(out.outputRef)} sx={{ p: 0.25 }}>
                            {state.previewVisible ? (
                              <HidePreviewIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <PreviewIcon sx={{ fontSize: 14 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>

                      <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                        {state.columns.map((col) => {
                          const isColSelected = selectedCols ? selectedCols.has(col.name) : true;
                          return (
                            <Box
                              key={col.name}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                py: 0.25,
                                px: 0.5,
                                borderRadius: 0.5,
                                opacity: isColSelected ? 1 : 0.5,
                                '&:hover': { bgcolor: alpha('#2563eb', 0.04) },
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={isColSelected}
                                onChange={() => toggleColumnFilter(out.outputRef, col.name)}
                                sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 14 } }}
                              />
                              {getTypeIcon(col.dtype)}
                              <Typography sx={{ fontSize: 11, fontFamily: '"Fira Code", monospace', flex: 1 }}>
                                {col.name}
                              </Typography>
                              <Chip
                                label={dtypeLabel(col.dtype)}
                                size="small"
                                sx={{ fontSize: 9, height: 16, fontWeight: 500 }}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  )}

                  {state?.columns && state.columns.length === 0 && !state.columnsLoading && (
                    <Typography variant="caption" color={state.columnsError ? 'error' : 'text.secondary'}>
                      {state.columnsError || 'No column metadata available'}
                    </Typography>
                  )}

                  {/* Quality details */}
                  {(() => {
                    const qs = qualitySummaries?.get(out.outputRef);
                    if (!qs) return null;
                    return (
                      <Box sx={{ mt: 1, pt: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" fontWeight={600} sx={{ fontSize: 10, color: '#475569', mb: 0.5, display: 'block' }}>
                          Quality
                        </Typography>
                        {qs.reviewMissing && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <NoReviewIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                            <Typography variant="caption" sx={{ fontSize: 10, color: '#94a3b8' }}>
                              No review data available
                            </Typography>
                          </Box>
                        )}
                        {qs.failedChecks.map((c, i) => (
                          <Box key={`fail-${c.name}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                            <ErrorIcon sx={{ fontSize: 11, color: '#dc2626' }} />
                            <Typography variant="caption" sx={{ fontSize: 10, color: '#dc2626', fontWeight: 600 }}>
                              {c.name}{c.measured != null ? `: ${(c.measured * 100).toFixed(1)}%` : ''}{c.threshold != null ? ` (threshold: ${(c.threshold * 100).toFixed(1)}%)` : ''}
                            </Typography>
                          </Box>
                        ))}
                        {qs.passedCheckCount > 0 && qs.failedChecks.length === 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                            <PassIcon sx={{ fontSize: 11, color: '#059669' }} />
                            <Typography variant="caption" sx={{ fontSize: 10, color: '#059669' }}>
                              {qs.passedCheckCount} check{qs.passedCheckCount !== 1 ? 's' : ''} passed
                            </Typography>
                          </Box>
                        )}
                        {qs.warnings.map((w, i) => (
                          <Box key={`warn-${w.checkName}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                            <WarnIcon sx={{ fontSize: 11, color: '#d97706' }} />
                            <Typography variant="caption" sx={{ fontSize: 10, color: '#d97706' }}>
                              {w.message}
                            </Typography>
                          </Box>
                        ))}
                        {qs.correctionCount > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                            <CorrectionIcon sx={{ fontSize: 11, color: '#2563eb' }} />
                            <Typography variant="caption" sx={{ fontSize: 10, color: '#2563eb' }}>
                              {qs.correctionCount} auto-correction{qs.correctionCount !== 1 ? 's' : ''} applied
                            </Typography>
                          </Box>
                        )}
                        {qs.rowCount != null && qs.rowCountBefore != null && qs.rowCountBefore !== qs.rowCount && (
                          <Typography variant="caption" sx={{ fontSize: 10, color: '#64748b', display: 'block', mt: 0.25 }}>
                            Rows: {qs.rowCountBefore.toLocaleString()} → {qs.rowCount.toLocaleString()}
                            {' '}({qs.rowCountBefore > 0 ? ((Math.abs(qs.rowCount - qs.rowCountBefore) / qs.rowCountBefore) * 100).toFixed(1) : '—'}% {qs.rowCount < qs.rowCountBefore ? 'reduction' : 'increase'})
                          </Typography>
                        )}
                      </Box>
                    );
                  })()}

                  {/* Hierarchical preview for assembled outputs — always shown
                      when the row is expanded (there's no "Preview" toggle
                      button for assembled outputs because they have no columns). */}
                  {isAssembled && out.assembledOutputPath && (
                    <Box sx={{ mt: 1, borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
                      <HierarchicalPreview
                        sessionId={sessionId}
                        outputRef={out.assembledOutputPath}
                        outputFormat={out.outputFormat}
                        byteSize={out.byteSize}
                        onDownload={() =>
                          downloadAssemblyOutput(sessionId, out.assembledOutputPath!).catch((err) => {
                            showToast('Download failed', err?.message || 'Failed to download assembly output', 'error');
                          })
                        }
                      />
                    </Box>
                  )}

                  {/* Inline preview for flat table outputs */}
                  <Collapse in={!isAssembled && !!state?.previewVisible} timeout="auto" unmountOnExit>
                    <Box sx={{ mt: 1, borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>

                      {/* Flat table preview for regular outputs */}
                      {!isAssembled && state?.previewLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                          <CircularProgress size={14} />
                          <Typography variant="caption" color="text.secondary">Loading preview...</Typography>
                        </Box>
                      )}
                      {!isAssembled && state?.previewRows && state.previewRows.length > 0 && (
                        <TableContainer sx={{ maxHeight: 200, overflow: 'auto' }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                {Object.keys(state.previewRows[0]).map((col) => (
                                  <TableCell
                                    key={col}
                                    sx={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      fontFamily: '"Fira Code", monospace',
                                      py: 0.5,
                                      px: 1,
                                      bgcolor: '#f1f5f9',
                                      whiteSpace: 'nowrap',
                                      maxWidth: 180,
                                    }}
                                  >
                                    {col}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {state.previewRows.map((row, idx) => (
                                <TableRow key={`row-${idx}`} sx={{ '&:last-child td': { border: 0 } }}>
                                  {Object.entries(row).map(([colName, val]: [string, any]) => (
                                    <TableCell
                                      key={colName}
                                      sx={{
                                        fontSize: 10,
                                        py: 0.25,
                                        px: 1,
                                        maxWidth: 180,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {val === null || val === undefined ? (
                                        <Chip label="null" size="small" sx={{ fontSize: 9, height: 16, color: '#94a3b8' }} />
                                      ) : (
                                        String(val)
                                      )}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                      {!isAssembled && state?.previewRows && state.previewRows.length === 0 && !state?.previewLoading && (
                        <Typography variant="caption" color="text.secondary">No data</Typography>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              </Collapse>
            </Box>
          );
        })}

        {/* Transform Outputs section — shown below mapped outputs when both exist */}
        {isMappedPhase && transformOutputs.length > 0 && (
          <>
            <Box sx={{ my: 1.5, borderTop: '1px solid', borderColor: 'divider' }} />
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, px: 0.5, mb: 0.5, display: 'block' }}
              >
                Transform Outputs
              </Typography>
            </Box>
            {transformOutputs.map((out) => {
              const isFailed = !!out.error;
              const isAssembled = !!out.assembledOutputPath;
              const chipColor = isFailed ? '#dc2626' : isAssembled ? '#a855f7' : (STEP_TYPE_COLORS[out.stepType] || '#64748b');
              const isChecked = selectedRefs.has(out.outputRef);
              const isExpanded = expandedRefs.has(out.outputRef);
              const state = expandedStates.get(out.outputRef);
              const selectedCols = columnFilters.get(out.outputRef);

              if (reviewStep === 'review' && !isChecked) return null;

              return (
                <Box key={out.outputRef || out.operationId} sx={{ mb: 0.75 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 0.75,
                      py: 1,
                      px: 1,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: isFailed ? alpha('#dc2626', 0.3) : isChecked ? alpha('#2563eb', 0.3) : 'divider',
                      bgcolor: isFailed ? alpha('#dc2626', 0.03) : isChecked ? alpha('#2563eb', 0.03) : 'transparent',
                      transition: 'border-color 0.15s, background-color 0.15s',
                      cursor: 'pointer',
                    }}
                    onClick={() => toggleExpand(out.outputRef)}
                  >
                    <Checkbox
                      size="small"
                      checked={isChecked}
                      onChange={() => toggleRef(out.outputRef)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ p: 0, mt: 0.25, '& .MuiSvgIcon-root': { fontSize: 18 } }}
                    />
                    {isFailed && (
                      <ErrorIcon sx={{ fontSize: 16, color: '#dc2626', mt: 0.25, flexShrink: 0 }} />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Tooltip title={out.outputRef} placement="top" arrow disableInteractive>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {out.displayName || out.outputRef}
                        </Typography>
                      </Tooltip>
                      <Typography variant="caption" sx={{ fontSize: 10, color: '#64748b', display: 'block' }}>
                        {out.description}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                        <Chip
                          label={out.stepType.replace(/_/g, ' ').toUpperCase()}
                          size="small"
                          sx={{ fontSize: 9, height: 18, fontWeight: 600, bgcolor: alpha(chipColor, 0.1), color: chipColor }}
                        />
                        {out.rowCount != null && (
                          <Typography variant="caption" sx={{ fontSize: 10, color: '#94a3b8' }}>
                            {out.rowCount.toLocaleString()} rows
                          </Typography>
                        )}
                        {out.columnCount != null && (
                          <Typography variant="caption" sx={{ fontSize: 10, color: '#94a3b8' }}>
                            · {out.columnCount} cols
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setDownloadMenuTable(out.outputRef); setDownloadMenuAnchor(e.currentTarget); }}
                        sx={{ p: 0.25 }}
                      >
                        <DownloadIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" sx={{ p: 0.25 }}>
                      {isExpanded ? <CollapseIcon sx={{ fontSize: 16 }} /> : <ExpandIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                  </Box>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 4, pr: 1, py: 0.75, bgcolor: '#fafbfc' }}>
                      {state?.columnsLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                          <CircularProgress size={14} />
                          <Typography variant="caption" color="text.secondary">Loading columns...</Typography>
                        </Box>
                      )}
                      {state?.columns && state.columns.length > 0 && (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                            <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11, color: 'text.secondary' }}>
                              Columns ({selectedCols ? `${selectedCols.size}/${state.columns.length}` : state.columns.length})
                            </Typography>
                          </Box>
                          <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                            {state.columns.map((col) => {
                              const isColSelected = selectedCols ? selectedCols.has(col.name) : true;
                              return (
                                <Box
                                  key={col.name}
                                  sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25, px: 0.5,
                                    borderRadius: 0.5, opacity: isColSelected ? 1 : 0.5,
                                    '&:hover': { bgcolor: alpha('#2563eb', 0.04) },
                                  }}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={isColSelected}
                                    onChange={() => toggleColumnFilter(out.outputRef, col.name)}
                                    sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 14 } }}
                                  />
                                  {getTypeIcon(col.dtype)}
                                  <Typography sx={{ fontSize: 11, fontFamily: '"Fira Code", monospace', flex: 1 }}>
                                    {col.name}
                                  </Typography>
                                  <Chip
                                    label={dtypeLabel(col.dtype)}
                                    size="small"
                                    sx={{ fontSize: 9, height: 16, fontWeight: 500 }}
                                  />
                                </Box>
                              );
                            })}
                          </Box>
                        </>
                      )}
                      {state?.columns && state.columns.length === 0 && !state.columnsLoading && (
                        <Typography variant="caption" color={state.columnsError ? 'error' : 'text.secondary'}>
                          {state.columnsError || 'No column metadata available'}
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </>
        )}
      </Box>

      {/* Download format menu */}
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={() => setDownloadMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleDownload(downloadMenuTable, 'csv')} sx={{ fontSize: 13 }}>
          CSV
        </MenuItem>
        <MenuItem onClick={() => handleDownload(downloadMenuTable, 'parquet')} sx={{ fontSize: 13 }}>
          Parquet
        </MenuItem>
      </Menu>

      {/* Sticky bottom bar */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: '#fafbfc',
        }}
      >
        {reviewStep === 'browse' ? (
          <>
            <Tooltip title={allSelected ? 'Deselect all' : 'Select all'} arrow>
              <IconButton size="small" onClick={toggleAll} sx={{ color: allSelected ? '#2563eb' : '#94a3b8' }}>
                <SelectAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="caption" sx={{ flex: 1, color: '#64748b' }}>
              {selectedRefs.size} selected
            </Typography>
            {onSaveSelection && (
              <Tooltip title="Add the selection as the Pipeline Output node on the canvas." arrow>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={savingSelection}
                    onClick={async () => {
                      try {
                        setSavingSelection(true);
                        await onSaveSelection(Array.from(selectedRefs));
                      } finally {
                        setSavingSelection(false);
                      }
                    }}
                    sx={{
                      fontSize: 11,
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: '#cbd5e1',
                      color: '#334155',
                      '&:hover': { borderColor: '#94a3b8', bgcolor: '#f1f5f9' },
                      borderRadius: 1,
                      px: 1.5,
                    }}
                  >
                    {savingSelection ? 'Saving…' : 'Save Selection'}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Button
              size="small"
              variant="contained"
              startIcon={<ExportIcon sx={{ fontSize: 14 }} />}
              disabled={outputs.length === 0}
              onClick={handleEnterReview}
              sx={{
                fontSize: 11,
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#7c3aed',
                '&:hover': { bgcolor: '#6d28d9' },
                borderRadius: 1,
                px: 1.5,
                boxShadow: 'none',
              }}
            >
              Export to Connector
            </Button>
          </>
        ) : (
          <>
            <Typography variant="caption" sx={{ flex: 1, color: failingSelectedCount > 0 ? '#dc2626' : '#64748b' }}>
              {selectedRefs.size} table{selectedRefs.size !== 1 ? 's' : ''} selected
              {failingSelectedCount > 0 && ` · ${failingSelectedCount} with issues`}
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<ExportIcon sx={{ fontSize: 14 }} />}
              disabled={selectedRefs.size === 0 || !canExport}
              onClick={handleExportSelected}
              sx={{
                fontSize: 11,
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: hasIssues ? '#dc2626' : showOverride ? '#d97706' : '#059669',
                '&:hover': { bgcolor: hasIssues ? '#b91c1c' : showOverride ? '#b45309' : '#047857' },
                borderRadius: 1,
                px: 1.5,
                boxShadow: 'none',
              }}
            >
              {hasIssues ? 'Export with Issues' : showOverride ? 'Export with Warnings' : 'Approve & Export'}
            </Button>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default PipelineOutputsDrawer;
